import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Box, Button, Chip, CircularProgress, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAuth } from '../../context/AuthContext';
import { signageService } from '../../services/signageService';
import { ISignageStore } from '../../types/signage';
import MediaThumb from './MediaThumb';
import StoreDialog from './StoreDialog';
import { isDefaultStore, storeDisplayName, storePath } from './storeRoute';

const StoreListPage: React.FC = () => {
  const navigate = useNavigate();
  const { hasRole } = useAuth();
  const canManage = hasRole(['signage:admin', 'admin']);

  const [stores, setStores] = useState<ISignageStore[]>([]);
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      setStores(await signageService.getStores());
      setError('');
    } catch (e: any) {
      setError(typeof e === 'string' ? e : 'Failed to load stores');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const text = query.trim().toLowerCase();
    return stores.filter((store) => !text || `${store.storeId} ${storeDisplayName(store.storeId, store.name)}`.toLowerCase().includes(text));
  }, [stores, query]);

  return (
    <Box sx={{ p: { xs: 2, sm: 3, md: 4 }, width: '100%' }}>
      <Stack direction="row" alignItems="center" sx={{ mb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 500, flex: 1 }}>
          Kiosk signage
        </Typography>
        {canManage && (
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
            Add store
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Each store has its own playlist. Choose a store to manage its images, GIFs and videos.
      </Typography>

      <TextField size="small" placeholder="Search by store ID or name" value={query} onChange={(e) => setQuery(e.target.value)} sx={{ mb: 2, width: { xs: '100%', sm: 340 } }} />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ borderRadius: 2, overflowX: 'auto' }}>
        {loading ? (
          <Box sx={{ p: 4, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableCell sx={{ width: 140 }}>Store ID</TableCell>
                <TableCell>Name</TableCell>
                <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Playlist</TableCell>
                <TableCell sx={{ width: 100 }}>Visible</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filtered.map((store) => (
                <TableRow key={store.storeId} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(storePath(store.storeId))} data-testid={`store-row-${store.storeId}`}>
                  <TableCell>
                    <Chip size="small" label={isDefaultStore(store.storeId) ? 'DEFAULT' : store.storeId} sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{storeDisplayName(store.storeId, store.name)}</TableCell>
                  <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      {store.previews.map((preview) => (
                        <MediaThumb key={preview.itemId} mediaType={preview.mediaType} fileId={preview.fileId} sourceUrl={preview.sourceUrl} width={46} height={26} />
                      ))}
                      {store.itemCount > store.previews.length && (
                        <Typography variant="caption" color="text.secondary">
                          +{store.itemCount - store.previews.length}
                        </Typography>
                      )}
                      {store.itemCount === 0 && (
                        <Typography variant="caption" color="text.secondary">
                          No items yet
                        </Typography>
                      )}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {store.visibleCount} / {store.itemCount}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow>
                  <TableCell colSpan={4} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{stores.length === 0 ? 'No stores yet.' : 'No stores match your search.'}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        )}
      </Paper>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
        "Default" is used by any store that has no visible items of its own.
      </Typography>

      <StoreDialog open={dialogOpen} store={null} onClose={() => setDialogOpen(false)} onSaved={(id) => navigate(storePath(id))} />
    </Box>
  );
};

export default StoreListPage;
